// =====  Vue Formulate  ========
// import VueFormulate from '@braid/vue-formulate';
import formsFormulateConfig from '@/components/forms/config/formulate';
import { merge } from 'lodash';

// all config for VueFormulate - custom components, etc.
// includes things for FormSchema version of it
// build the config object here, export it, import into main.js and register with Vue.use(VueFormulate, vueFormulateOptions);

// https://vueformulate.com/guide/validation/#custom-validation-rules
// function myRule (context, ...args) { return boolean; }  // true = pass validation, false = fail
const customRules = {
  // must be camelCase or lowercase -- no underscores!
  optionalBefore: ({ value }, compare = false) => {
    // the default 'before' rule fails if the field is empty
    // this version passes if empty, or if before compare date
    // everything is exactly same as normal vue-formulate 'before' rule, except last line defaults to true if isNaN(fieldValue)
    // if you also want it to fail if empty, set the field to be required!
    const timestamp = Date.parse(compare || new Date())
    const fieldValue = Date.parse(value)
    return Promise.resolve(isNaN(fieldValue) ? true : (fieldValue < timestamp))
  },
  optionalAfter: ({ value }, compare = false) => {
    // the default 'after' rule fails if the field is empty
    // this version passes if empty, or if after compare date
    // everything is exactly same as normal vue-formulate 'after' rule, except last line defaults to true if isNaN(fieldValue)
    // if you also want it to fail if empty, set the field to be required!
    const timestamp = Date.parse(compare || new Date())
    const fieldValue = Date.parse(value)
    return Promise.resolve(isNaN(fieldValue) ? true : (fieldValue > timestamp))
  },
};


const vueFormulateOptions = merge(
  {
    library: {
      phonenumber: {
        classification: 'text',
        component: 'FormulatePhoneNumber',
        slotProps: {
          component: ['countryCode'], // this makes sure you can pass country-code prop to custom field
        },
      },
      phonenumberfull: {
        classification: 'phonenumbercustom', // we don't use 'text' because that then applies some difficult to override styles
        component: 'FormulateFullPhoneNumber',
        slotProps: {
          component: ['countries', 'preferredCountryCode', 'noRemove']
        }
      },
      vendorselect: {
        classification: 'select',
        component: 'FormulateVendorSelect',
      },
      vendorcontactpicker: {
        classification: 'select',
        component: 'FormulateVendorContactPicker',
        slotProps: {
          component: ['vendorId', 'vendorField', 'noAdd', 'selectDefaults', 'showDefaults', 'disabledField'],
        },
      },
      vendorsitepicker: {
        classification: 'select',
        component: 'FormulateVendorSitePicker',
        slotProps: {
          component: ['vendorId', 'vendorField'],
        },
      },
      tenantuserpicker: {
        classification: 'select',
        component: 'FormulateTenantUserPicker',
        slotProps: {
          component: ['selectionLimit', 'filters', 'disabledField'],
        },
      },
      advancedselect: {
        // supports single OR multi-select
        classification: 'select',
        component: 'FormulateAdvancedSelect',
        slotProps: {
          component: [
            'url',
            'multiple',
            'selectionLimit',
            'apiEndpoint',
            'apiEndpointArgs',
            'resultsKey',
            'labelField',
            'valueField',
            'disabledField',
            'dataKey',
            'options',
            'showFilter',
            'inputType',
            'showToggleAll',
          ],
        },
      },
      lazy_autocomplete: {
        // for picking from MANY options (ex: Products) -- nothing is loaded until the user types minChars characters, then it searches the API
        // pass only the currently-selected option(s) as options (for their labels), not the full list
        classification: 'select',
        component: 'FormulateLazyAutocomplete',
        slotProps: {
          component: [
            'multiple',
            'selectionLimit',
            'apiEndpoint',
            'apiEndpointArgs',
            'url',
            'resultsKey',
            'searchParam',
            'extraParams',
            'pageSizeParam',
            'pageSize',
            'minChars',
            'debounceMs',
            'labelField',
            'sublabelField',
            'valueField',
            'dataKey',
            'disabledField',
          ],
        },
      },
      badgevariantpicker: {
        classification: 'text', // picking 'box' makes things strange...
        component: 'FormulateBadgePicker',
        slotProps: {
          component: ['badgeText', 'badgeClass', 'options'],
        },
      },
      htmlcontent: {
        classification: 'text',
        component: 'FormulateHtmlContent',
        slotProps: {
          component: ['content'],
        },
      },
      s3fileuploader: {
        classification: 'text', /// ??? or group?  if it is 'text' then it doesn't pass on all props that would be relevant to files, like 'multiple' - just know that
        component: 'FormulateS3FileUploader',
        slotProps: {
          component: ['fileCategory', 'parentId', 'maxItems', 'single', 'accept', 'imageOnly', 'maxFilesize', 'maxTotalFilesize', 'hideLimits'],
        },
      },
      snippet_textarea: {
        classification: 'textarea',
        component: 'FormulateSnippetTextarea',
        slotProps: {
          component: ['snippetArea', 'snippetButtonText'],
        },
      },
      fishbone_diagram: {
        classification: 'text', // just to be generic
        component: 'FormulateFishboneDiagram',
        slotProps: {
          component: ['fishboneData'],
        },
      },
      multi_value_chips: {
        classification: 'text', // just to be generic
        component: 'FormulateChips',
        slotProps: {
          component: ['addOnBlur', 'allowDuplicate', 'max', 'separator', 'placeholder'],
        },
      },
      tenant_role_matrix: {
        classification: 'text', // just to be generic - the value is an object { role_slug: [tu_id, ...] }
        component: 'FormulateTenantRoleMatrix',
        slotProps: {
          component: ['contacts', 'contactTypes'],
        },
      },
      company_sites_manager: {
        classification: 'text', // just to be generic - the value is the list of what is still missing in the company's sites
        component: 'FormulateCompanySitesManager',
        slotProps: {
          component: ['companyId', 'noShadow'],
        },
      },
      company_contacts_manager: {
        classification: 'text', // just to be generic - no value of its own (the component saves the contacts)
        component: 'FormulateCompanyContactsManager',
        slotProps: {
          component: ['companyId', 'hideFields', 'noShadow'],
        },
      },
      money: {
        classification: 'text',
        component: 'FormulateMoney',
        slotProps: {
          component: ['fixedCurrency', 'currencyOptions', 'decimalPlaces', 'hideCurrency'],
        },
      },
      integer: {
        classification: 'text',
        component: 'FormulateInteger',
        slotProps: {
          component: ['decimalPlaces'], // also has min, max, step - but those should be standard anyways
        },
      },
      bootstrap_date: {
        classification: 'date',
        component: 'FormulateBootstrapDate',
        slotProps: {
          component: ['inline', 'componentProps'],  // min, max are props, because they are props in the standard formulate datepicker
        },
      },
      ag_grid: {
        classification: 'text',  // or group or what???
        component: 'FormulateAgGrid',
        slotProps: {
          component: ['gridOptions', 'cols', 'gridContext', 'colConfig', 'gridHeight', 'noAdd', 'noRemove', 'noReorder'], // columnDefs could be passed in, or built from children
        },
      },
    },
    slotComponents: {
      label: 'FormulateCustomLabel',
    },
    slotProps: {
      label: ['labelPopover', 'hideLabelIcons', 'labelHideRequired', 'requiredText'], // custom props for our FormulateCustomLabel component
    },
    rules: {
      // NOTE: rule names must be camelCase or lowercase -- NO UNDERSCORES (they get converted to camelCase automatically, and formulate seems to have issues with that)
      ...customRules,
    }
    //uploader: // you could pass a single async function here to be used for all uploads (but you have to set category of S3File upload somehow)
    /* classes: {
        // use strings to replace their default, array to append to, or a function
        // can also do this on a per-component level
        uploadArea: ['bg-gray-100'],
    }, */
  },
  formsFormulateConfig,
);

export default vueFormulateOptions;
